import { toast } from 'sonner'
import { useUpdatePurchaseOrder } from '@/features/purchase-orders/hooks'
import { POEstimatedPackageFacts } from '@/components/po-estimated-package-facts'
import { hasEstimatedPackageFacts } from '@/components/po-estimated-package-facts-model'
import { formatApiErrorMessage } from '@/lib/api-error'
import { POAccessorialsControl } from './accessorials'
import { DetailChoice } from './source'

const FREIGHT_MODES = [
  { value: 'parcel', label: 'Parcel (UPS/FedEx)' },
  { value: 'ltl', label: 'LTL (pallet/skid)' },
  { value: 'auto', label: 'Auto-detect (by weight)' },
] as const

const FREIGHT_OWNERS = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'usars', label: 'USA-RS' },
] as const

// Who pays freight the manufacturer books (USA-1658); freight we book is always ours.
const FREIGHT_PAYERS = [
  { value: 'USA-RS UPS account', label: 'USA-RS (our UPS account)' },
  { value: 'Prepay & add', label: 'USA-RS (prepay & add)' },
  { value: 'Manufacturer charges freight', label: 'USA-RS (their freight charge)' },
  { value: 'Manufacturer', label: 'Manufacturer (free freight)' },
] as const

type FreightPayer = (typeof FREIGHT_PAYERS)[number]['value']

function freightPayer(billing: string | null): FreightPayer {
  return FREIGHT_PAYERS.find((option) => option.value === billing)?.value ?? 'Manufacturer'
}

function FreightModeBadge({
  poUuid,
  freightMode,
  estimatedWeightLbs,
}: {
  poUuid: string
  freightMode: 'parcel' | 'ltl' | null
  estimatedWeightLbs: string | null
}) {
  const updatePO = useUpdatePurchaseOrder(poUuid)

  const display =
    freightMode === 'parcel' ? 'Parcel' : freightMode === 'ltl' ? 'LTL' : 'Auto-detect'

  // The same quiet text-and-arrow picker as Source and Freight beside it (USA-1536).
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <DetailChoice
        label="Freight mode"
        value={freightMode ?? 'auto'}
        display={display}
        options={FREIGHT_MODES}
        disabled={updatePO.isPending}
        onChange={(next) => {
          const newMode = next === 'auto' ? null : next
          if (newMode === freightMode) return
          updatePO.mutate(
            { freight_mode: newMode },
            {
              onSuccess: () => {
                toast.success(
                  newMode == null
                    ? 'Freight mode reset to auto-detect'
                    : `Freight mode set to ${newMode === 'parcel' ? 'Parcel' : 'LTL'}`,
                )
              },
              onError: (err) => {
                const detail =
                  err && typeof err === 'object' && 'detail' in err
                    ? String((err as { detail: unknown }).detail)
                    : 'Failed to update freight mode'
                toast.error(detail)
              },
            },
          )
        }}
      />
      {estimatedWeightLbs != null && (
        <span className="text-xs text-muted-foreground">
          Est. {Number(estimatedWeightLbs).toFixed(1)} lbs
        </span>
      )}
    </div>
  )
}

export function FreightResponsibilityControl({
  poUuid,
  weBookFreight,
  freightBilling,
  freightMode,
  estimatedWeightLbs,
  estimatedPackageFacts,
  accessorialSnapshot,
}: {
  poUuid: string
  weBookFreight: boolean
  freightBilling: string | null
  freightMode: 'parcel' | 'ltl' | null
  estimatedWeightLbs: string | null
  estimatedPackageFacts: Record<string, unknown>
  accessorialSnapshot: Record<string, unknown>
}) {
  const updatePO = useUpdatePurchaseOrder(poUuid)
  const owner: 'manufacturer' | 'usars' = weBookFreight ? 'usars' : 'manufacturer'
  const payer = freightPayer(freightBilling)

  // Rows for the PO details grid (label, then value).
  return (
    <>
      <dt className="pt-1.5 text-xs text-muted-foreground">Booked by</dt>
      <dd className="min-w-0">
        <DetailChoice
          label="Freight booked by"
          value={owner}
          display={FREIGHT_OWNERS.find((option) => option.value === owner)?.label}
          options={FREIGHT_OWNERS}
          disabled={updatePO.isPending}
          onChange={(nextOwner) => {
            const nextWeBookFreight = nextOwner === 'usars'
            updatePO.mutate(
              { we_book_freight: nextWeBookFreight },
              {
                onSuccess: () =>
                  toast.success(
                    nextWeBookFreight ? 'USA-RS will book freight' : 'Manufacturer will book freight',
                  ),
                onError: (error) =>
                  toast.error(
                    formatApiErrorMessage('Failed to update freight responsibility', error),
                  ),
              },
            )
          }}
        />
      </dd>
      <dt className="pt-1.5 text-xs text-muted-foreground">Paid by</dt>
      <dd className="min-w-0">
        {weBookFreight ? (
          <span className="flex h-8 items-center font-medium">USA-RS</span>
        ) : (
          <DetailChoice
            label="Freight paid by"
            value={payer}
            display={FREIGHT_PAYERS.find((option) => option.value === payer)?.label}
            options={FREIGHT_PAYERS}
            disabled={updatePO.isPending}
            onChange={(nextPayer) => {
              if (nextPayer === payer) return
              updatePO.mutate(
                { freight_billing: nextPayer },
                {
                  onSuccess: () =>
                    toast.success(
                      `Freight paid by ${FREIGHT_PAYERS.find((option) => option.value === nextPayer)?.label}`,
                    ),
                  onError: (error) =>
                    toast.error(formatApiErrorMessage('Failed to update who pays freight', error)),
                },
              )
            }}
          />
        )}
      </dd>
      {weBookFreight ? (
        <>
          <dt className="pt-1.5 text-xs text-muted-foreground">Freight mode</dt>
          <dd className="min-w-0">
            <FreightModeBadge
              poUuid={poUuid}
              freightMode={freightMode}
              estimatedWeightLbs={estimatedWeightLbs}
            />
          </dd>
        </>
      ) : null}
      {freightMode !== 'parcel' ? (
        <POAccessorialsControl poUuid={poUuid} snapshot={accessorialSnapshot} />
      ) : null}
      {hasEstimatedPackageFacts(estimatedPackageFacts) ? (
        <>
          <dt className="pt-0.5 text-xs text-muted-foreground">Packages</dt>
          <dd className="min-w-0">
            <POEstimatedPackageFacts facts={estimatedPackageFacts} />
          </dd>
        </>
      ) : null}
    </>
  )
}
